import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Plus, BookOpen, Trash2 } from "lucide-react";

export default function TeacherExamList() {
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const load = () => api.get("/exams").then((r) => setExams(r.data));
  useEffect(() => {
    load();
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
  }, []);
  const classNameById = Object.fromEntries(classes.map((c) => [c.id, c.name]));

  const del = async (id) => {
    if (!window.confirm("Delete this exam?")) return;
    await api.delete(`/exams/${id}`);
    load();
  };

  return (
    <AppShell title="Exams" subtitle="Create, publish and manage your assessments.">
      <div className="flex justify-end mb-4">
        <Link to="/teacher/exams/new" className="btn-primary text-sm" data-testid="new-exam-btn">
          <Plus className="w-4 h-4" /> New exam
        </Link>
      </div>
      <div className="qs-card overflow-hidden" data-testid="teacher-exam-table">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-3 font-bold">Title</th>
              <th className="text-left px-4 py-3 font-bold">Subject</th>
              <th className="text-left px-4 py-3 font-bold">Class</th>
              <th className="text-left px-4 py-3 font-bold">Duration</th>
              <th className="text-left px-4 py-3 font-bold">Questions</th>
              <th className="text-left px-4 py-3 font-bold">Status</th>
              <th className="text-right px-4 py-3 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {exams.map((e) => (
              <tr key={e.id} data-testid={`exam-row-${e.id}`}>
                <td className="px-4 py-3 font-semibold text-slate-800">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center">
                      <BookOpen className="w-4 h-4" />
                    </div>
                    {e.title}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-700">{e.subject}</td>
                <td className="px-4 py-3 text-slate-700">
                  {e.class_id ? (
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                      {classNameById[e.class_id] || "Assigned"}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">Unassigned</span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-700">{e.duration_min} min</td>
                <td className="px-4 py-3 text-slate-700">{e.questions?.length || 0}</td>
                <td className="px-4 py-3">
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      e.is_published ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {e.is_published ? "Published" : "Draft"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/teacher/exams/${e.id}`} className="text-indigo-600 font-semibold mr-4" data-testid={`edit-exam-${e.id}`}>
                    Edit
                  </Link>
                  <button onClick={() => del(e.id)} className="text-rose-500 inline-flex items-center gap-1" data-testid={`delete-exam-${e.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {exams.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-12 text-slate-500" data-testid="no-teacher-exams">
                  No exams yet — create your first one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
